import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { cartAPI } from '../../services/storeClubAPI';
import ClubLayout from '../../components/StoreClub/ClubLayout';
import LauraChat from '../../components/StoreClub/LauraChat';

interface Product {
  id: number;
  name: string;
  brand: string;
  size_lbs: number;
  price: string;
  is_byoc: boolean;
  form_type?: string;
  category: string;
}

interface CartItem {
  id: number;
  product: Product;
  quantity: number;
  line_total: string;
}

interface Cart {
  items: CartItem[];
  subtotal: string;
  card_processing_fee: string;
  total: string;
  item_count: number;
}

const ClubCart: React.FC = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);
  const [updatingItem, setUpdatingItem] = useState<number | null>(null);

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = async () => {
    try {
      const data = await cartAPI.get();
      setCart(data);
    } catch (error) {
      console.error('Failed to load cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateQuantity = async (itemId: number, quantity: number) => {
    if (quantity < 1) {
      await handleRemoveItem(itemId);
      return;
    }

    setUpdatingItem(itemId);
    try {
      await cartAPI.updateItem(itemId, quantity);
      await loadCart();
    } catch (error) {
      console.error('Failed to update quantity:', error);
    } finally {
      setUpdatingItem(null);
    }
  };

  const handleRemoveItem = async (itemId: number) => {
    setUpdatingItem(itemId);
    try {
      await cartAPI.removeItem(itemId);
      await loadCart();
    } catch (error) {
      console.error('Failed to remove item:', error);
    } finally {
      setUpdatingItem(null);
    }
  };

  const handleCheckout = () => {
    navigate('/club/checkout');
  };

  if (loading) {
    return (
      <ClubLayout>
        <div style={styles.loading}>Loading cart...</div>
      </ClubLayout>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <ClubLayout>
        <div style={styles.emptyCart}>
          <div style={styles.emptyIcon}>🛒</div>
          <h2 style={styles.emptyTitle}>Your cart is empty</h2>
          <p style={styles.emptyText}>Time to stock up on feed!</p>
          <Link to="/club/shop" style={styles.shopButton}>
            Browse Products
          </Link>
        </div>
      </ClubLayout>
    );
  }

  return (
    <ClubLayout>
      <LauraChat message="Ready to check out? Choose locker pickup for 24/7 convenience - just a small 3% fee!" />

      <div style={styles.content}>
        <h2 style={styles.heading}>Your Cart</h2>

        <div style={styles.items}>
          {cart.items.map((item) => (
            <div key={item.id} style={styles.itemCard}>
              <div style={styles.itemInfo}>
                <span style={styles.itemName}>{item.product.name}</span>
                <span style={styles.itemDetails}>
                  {item.product.brand && `${item.product.brand} • `}
                  {item.product.size_lbs ? `${Math.round(item.product.size_lbs)}#` : ''}
                  {item.product.form_type && ` • ${item.product.form_type}`}
                </span>
                <span style={styles.itemPrice}>${item.product.price} each</span>
              </div>
              <div style={styles.itemActions}>
                <div style={styles.quantityControl}>
                  <button
                    style={styles.quantityButton}
                    onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                    disabled={updatingItem === item.id}
                  >
                    -
                  </button>
                  <span style={styles.quantity}>{item.quantity}</span>
                  <button
                    style={styles.quantityButton}
                    onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                    disabled={updatingItem === item.id}
                  >
                    +
                  </button>
                </div>
                <span style={styles.lineTotal}>${item.line_total}</span>
              </div>
            </div>
          ))}
        </div>

        <div style={styles.summary}>
          <div style={styles.totalRow}>
            <span>Subtotal</span>
            <span>${cart.subtotal}</span>
          </div>
        </div>

        <button
          style={styles.checkoutButton}
          onClick={handleCheckout}
        >
          Proceed to Checkout
        </button>

        <Link to="/club/shop" style={styles.continueLink}>
          ← Continue Shopping
        </Link>
      </div>
    </ClubLayout>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  content: {
    padding: '1rem',
    paddingBottom: '5rem',
    maxWidth: '600px',
    margin: '0 auto',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
    color: '#666',
    fontSize: '1.125rem',
  },
  emptyCart: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '60vh',
    padding: '2rem',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '4rem',
    marginBottom: '1rem',
  },
  emptyTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#333',
    margin: '0 0 0.5rem 0',
  },
  emptyText: {
    color: '#666',
    margin: '0 0 1.5rem 0',
  },
  shopButton: {
    backgroundColor: '#508590',
    color: '#fff',
    padding: '0.875rem 2rem',
    borderRadius: '8px',
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: '1rem',
  },
  heading: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#333',
    margin: '0 0 1rem 0',
  },
  items: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginBottom: '1.5rem',
  },
  itemCard: {
    backgroundColor: '#fff',
    padding: '1rem',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  itemInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
    marginBottom: '0.75rem',
  },
  itemName: {
    fontWeight: 600,
    color: '#333',
    fontSize: '1rem',
  },
  itemDetails: {
    fontSize: '0.8125rem',
    color: '#666',
  },
  itemPrice: {
    fontSize: '0.8125rem',
    color: '#508590',
  },
  itemActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  quantityControl: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    padding: '0.25rem',
  },
  quantityButton: {
    width: '32px',
    height: '32px',
    border: 'none',
    backgroundColor: '#fff',
    borderRadius: '6px',
    fontSize: '1.25rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    color: '#508590',
  },
  quantity: {
    minWidth: '24px',
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '1rem',
  },
  lineTotal: {
    fontWeight: 'bold',
    color: '#E73C3E',
    fontSize: '1.125rem',
  },
  pickupSection: {
    marginBottom: '1.5rem',
  },
  sectionTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#333',
    margin: '0 0 0.75rem 0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  pickupOptions: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.75rem',
  },
  pickupOption: {
    display: 'flex',
    flexDirection: 'column',
    padding: '1rem',
    border: '2px solid #e0e0e0',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  pickupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  pickupIcon: {
    fontSize: '1.5rem',
  },
  pickupRadio: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '2px solid #508590',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioInner: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#508590',
  },
  pickupTitle: {
    fontWeight: 600,
    color: '#333',
    fontSize: '0.9375rem',
    marginBottom: '0.25rem',
  },
  pickupDesc: {
    fontSize: '0.75rem',
    color: '#666',
    marginBottom: '0.5rem',
  },
  pickupFee: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#508590',
  },
  notesSection: {
    marginBottom: '1.5rem',
  },
  notesLabel: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#333',
    marginBottom: '0.5rem',
  },
  notesInput: {
    width: '100%',
    padding: '0.75rem 1rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    resize: 'none',
    boxSizing: 'border-box',
    fontFamily: "'Open Sans', sans-serif",
  },
  summary: {
    backgroundColor: '#fff',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    color: '#666',
    fontSize: '0.9375rem',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.75rem 0 0',
    borderTop: '1px solid #e0e0e0',
    marginTop: '0.5rem',
    fontWeight: 'bold',
    fontSize: '1.125rem',
    color: '#333',
  },
  checkoutButton: {
    width: '100%',
    backgroundColor: '#E73C3E',
    color: '#fff',
    border: 'none',
    padding: '1rem',
    borderRadius: '8px',
    fontSize: '1.125rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: '1rem',
    fontFamily: "'Open Sans', sans-serif",
  },
  continueLink: {
    display: 'block',
    textAlign: 'center',
    color: '#508590',
    textDecoration: 'none',
    fontSize: '0.9375rem',
    fontWeight: 500,
  },
};

export default ClubCart;
